import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowLeft, Save, Lock, Globe } from 'lucide-react';

const AccountSettings = ({ user, token, language, onLogout, onToggleLanguage, onUserUpdate }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    mobile: user?.mobile || '',
    language: user?.language || 'en',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Note: Backend endpoint needs to be created for profile update
    // For now, just show success message
    setTimeout(() => {
      setSuccess('Profile updated successfully! (Backend integration pending)');
      setLoading(false);
      onUserUpdate();
    }, 1000);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    // Note: Backend endpoint needs to be created for password update
    setTimeout(() => {
      setSuccess('Password updated successfully! (Backend integration pending)');
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setLoading(false);
    }, 1000);
  };

  const translations = {
    en: {
      title: 'Account Settings',
      back: 'Back to Dashboard',
      profileSettings: 'Profile Settings',
      mobile: 'Mobile Number',
      language: 'Preferred Language',
      saveChanges: 'Save Changes',
      passwordSettings: 'Password Settings',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      updatePassword: 'Update Password',
      saving: 'Saving...'
    },
    hi: {
      title: 'खाता सेटिंग्स',
      back: 'डैशबोर्ड पर वापस जाएं',
      profileSettings: 'प्रोफ़ाइल सेटिंग्स',
      mobile: 'मोबाइल नंबर',
      language: 'पसंदीदा भाषा',
      saveChanges: 'परिवर्तन सहेजें',
      passwordSettings: 'पासवर्ड सेटिंग्स',
      currentPassword: 'वर्तमान पासवर्ड',
      newPassword: 'नया पासवर्ड',
      confirmPassword: 'नए पासवर्ड की पुष्टि करें',
      updatePassword: 'पासवर्ड अपडेट करें',
      saving: 'सहेजा जा रहा है...'
    }
  };

  const t = translations[language];

  return (
    <div className="settings-page">
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <div className="header-actions">
            <button className="lang-toggle" onClick={onToggleLanguage}>
              {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="settings-content">
        <div className="container">
          <div className="page-header">
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={18} /> {t.back}
            </button>
            <h2>{t.title}</h2>
          </div>

          {success && <div className="success-message">{success}</div>}
          {error && <div className="error-message">{error}</div>}

          <div className="settings-grid">
            {/* Profile Settings */}
            <div className="settings-card">
              <div className="card-header">
                <Globe size={24} />
                <h3>{t.profileSettings}</h3>
              </div>
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group">
                  <label>{t.mobile}</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>
                <div className="form-group">
                  <label>{t.language}</label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                  >
                    <option value="en">English</option>
                    <option value="hi">हिंदी (Hindi)</option>
                  </select>
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <Save size={18} />
                  {loading ? t.saving : t.saveChanges}
                </button>
              </form>
            </div>

            {/* Password Settings */}
            <div className="settings-card">
              <div className="card-header">
                <Lock size={24} />
                <h3>{t.passwordSettings}</h3>
              </div>
              <form onSubmit={handleUpdatePassword}>
                <div className="form-group">
                  <label>{t.currentPassword}</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                  />
                </div>
                <div className="form-group">
                  <label>{t.newPassword}</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                  />
                </div>
                <div className="form-group">
                  <label>{t.confirmPassword}</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <Lock size={18} />
                  {loading ? t.saving : t.updatePassword}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
